import React from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { format, eachDayOfInterval, getDay, startOfMonth, endOfMonth, isSameDay, isWithinInterval, addMonths, subMonths, isValid } from 'date-fns';
import { getAllTimeSlots } from '../../utils/roleHelpers';

const ScheduleCalendar = ({ 
    calDate, setCalDate, 
    scheduleStartDate, scheduleEndDate, // Specific date props
    scheduleStartTime, scheduleEndTime, castingInterval, // NEW PROPS for time slots
    watchedWeekDaysOff, dateSpecificOffSlots, toggleSlotAvailability, isCurrentVideo 
}) => {
    // console.log('ScheduleCalendar Rendered with:', { calDate, scheduleStartDate, scheduleEndDate, scheduleStartTime, scheduleEndTime, castingInterval, watchedWeekDaysOff, dateSpecificOffSlots });

    // Use specific date props directly, ensuring they are valid
    const safeSDate = isValid(scheduleStartDate) ? scheduleStartDate : null; 
    const safeEDate = isValid(scheduleEndDate) ? scheduleEndDate : null;

    // Ensure calDate is valid, default to safeSDate if available, otherwise current date
    const currentCalDate = isValid(calDate) ? calDate : (safeSDate || new Date());

    const currentMonthStart = startOfMonth(currentCalDate);
    const currentMonthEnd = endOfMonth(currentCalDate);

    const days = eachDayOfInterval({ start: currentMonthStart, end: currentMonthEnd });
    const offDayIndexes = (watchedWeekDaysOff || []).map(d => parseInt(d.value)); 
    
    // Pass dynamic time slot values from props to getAllTimeSlots
    const allTimeSlots = getAllTimeSlots({ 
        start_time: scheduleStartTime, 
        end_time: scheduleEndTime, 
        casting_time: castingInterval,
    }, isCurrentVideo);

    // Helper to clamp a date within the schedule's start and end dates (not strictly needed for month nav, but good for consistency)
    const clampDateToScheduleRange = (date) => {
        if (!safeSDate || !safeEDate) return date; // No range, no clamping
        if (date < safeSDate) return safeSDate;
        if (date > safeEDate) return safeEDate;
        return date;
    };

    const handlePreviousMonth = () => {
        let newCalDate = subMonths(currentCalDate, 1);
        // Clamp the new month to be within the schedule's active range
        if (safeSDate && newCalDate < startOfMonth(safeSDate)) {
            newCalDate = startOfMonth(safeSDate);
        }
        setCalDate(newCalDate);
    };

    const handleNextMonth = () => {
        let newCalDate = addMonths(currentCalDate, 1);
        // Clamp the new month to be within the schedule's active range
        if (safeEDate && newCalDate > endOfMonth(safeEDate)) {
            newCalDate = endOfMonth(safeEDate);
        }
        setCalDate(newCalDate);
    };

    // Calculate if the previous/next month buttons should be disabled
    const isPrevMonthDisabled = safeSDate && currentMonthStart <= startOfMonth(safeSDate);
    const isNextMonthDisabled = safeEDate && currentMonthEnd >= endOfMonth(safeEDate);

    // If calDate gets outside the current active range (e.g., due to form date changes)
    // Adjust it to be within the range. This useEffect ensures calDate stays visually within the schedule range.
    React.useEffect(() => {
        if (safeSDate && safeEDate && !isWithinInterval(currentCalDate, { start: safeSDate, end: safeEDate })) {
            // If currentCalDate is before the start, set it to start
            if (currentCalDate < safeSDate) {
                setCalDate(safeSDate);
            } 
            // If currentCalDate is after the end, set it to end
            else if (currentCalDate > safeEDate) {
                setCalDate(safeEDate);
            }
        }
    }, [currentCalDate, safeSDate, safeEDate, setCalDate]);


    return (
        <div className="animate-fade-in">
            <h6 className="text-white mb-3 d-flex justify-content-between align-items-center">
                <Button variant='link' className='text-white p-1' onClick={handlePreviousMonth} disabled={isPrevMonthDisabled}><FaChevronLeft/></Button>
                {format(currentCalDate, 'MMMM yyyy')}
                <Button variant='link' className='text-white p-1' onClick={handleNextMonth} disabled={isNextMonthDisabled}><FaChevronRight/></Button>
            </h6>
            <div className="custom-calendar-grid mb-4">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="cal-header">{d}</div>)}
                {days.map((day, i) => {
                    const dayIndex = getDay(day);
                    const isWeekDayOff = offDayIndexes.includes(dayIndex);
                    
                    // Check if day is within the actual selected start_date and end_date range
                    const inScheduleRange = safeSDate && safeEDate && isWithinInterval(day, { start: safeSDate, end: safeEDate });
                    const isSelected = isValid(currentCalDate) && isSameDay(currentCalDate, day); 

                    let classNames = 'cal-day ';
                    
                    if (!inScheduleRange) {
                        classNames += 'out-range '; // Clearly mark days outside the schedule's active range
                    } else if (isWeekDayOff) {
                        classNames += 'disabled-day ';
                    } else if (isSelected) {
                        classNames += 'active ';
                    }

                    return (
                        <div key={i} 
                             className={classNames} 
                             onClick={() => { 
                                 // Only allow selecting a day if it's within the active schedule range and not a weekday off
                                 if (inScheduleRange && !isWeekDayOff) {
                                     setCalDate(day); 
                                 }
                             }}
                        >
                            {format(day, 'd')}
                        </div>
                    );
                })}
            </div>
            <h6 className="text-white small mb-2">Time Slots ({format(currentCalDate, 'dd MMM')})</h6>
            <div className="slot-legend">
                <div className="legend-item"><div className="legend-color bg-success"></div> Available</div>
                <div className="legend-item"><div className="legend-color bg-danger"></div> Break</div>
            </div>
            <div className="time-slot-grid-calculated d-flex flex-wrap gap-2">
                {allTimeSlots.length > 0 ? allTimeSlots.map((time) => { 
                    const isBreak = (dateSpecificOffSlots[format(currentCalDate, 'yyyy-MM-dd')] || []).includes(time); 
                    return (
                        <div key={time} onClick={() => toggleSlotAvailability(time)} className={`time-slot-box ${isBreak ? 'slot-break' : 'slot-available'}`} title={isBreak ? "Mark Available" : "Mark Break"}>
                            {time}
                        </div>
                    ); 
                }) : <small className="text-muted">No slots available for this time range and interval.</small>}
            </div>
        </div>
    );
};

export default ScheduleCalendar;